import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildReturnPrint } from "@/modules/docs/print/doc-print-math";
import { getReturn, voidReturn } from "@/shared/api/returns";
import { returnLineColumns } from "./return-detail-columns";

export function ReturnDetailPage() {
  const { companySlug, returnId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getReturn>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (!companySlug || !returnId) return;
    setData(await getReturn(companySlug, returnId));
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !returnId) return;
    getReturn(companySlug, returnId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, returnId, navigate]);

  useRecordRecentDoc({
    companySlug,
    kind: "return",
    id: data?.return.id,
    label: data?.return.number,
    path:
      companySlug && data?.return.id
        ? `/c/${companySlug}/returns/${data.return.id}`
        : undefined,
    hint: data?.return.sourceNumber
      ? `← ${data.return.sourceNumber}`
      : data?.return.sourceType,
  });

  async function onVoid() {
    if (!companySlug || !returnId) return;
    const ok = await confirmUi({
      title: "Void this return?",
      description:
        "Restock and refund/credit will reverse. The number is kept (gap OK).",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    const idempotencyKey = `ret-void-${crypto.randomUUID()}`;
    try {
      await voidReturn(companySlug, returnId, { idempotencyKey });
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !returnId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading return…</Alert>
        )}
      </PageFrame>
    );
  }

  const { return: ret, lines } = data;
  const printData = buildReturnPrint({
    number: ret.number,
    sourceType: ret.sourceType,
    sourceNumber: ret.sourceNumber,
    lifecycle: ret.lifecycle,
    settleMode: ret.settleMode,
    restock: ret.restock,
    netTotal: ret.netTotal,
    taxTotal: ret.taxTotal,
    grossTotal: ret.grossTotal,
    issuedAt: ret.postedAt,
    lines: lines.map((l) => ({
      description: l.description ?? "Item",
      qty: l.qty,
      unitPrice: l.unitPrice,
      lineGross: l.lineGross,
    })),
  });

  const sourceHref =
    ret.sourceType === "sales_order"
      ? `/c/${companySlug}/sales/orders/${ret.sourceId}`
      : ret.sourceType === "pos_ticket"
        ? `/c/${companySlug}/pos/tickets/${ret.sourceId}`
        : null;

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/returns`}
            listLabel="Returns"
          />
        </div>
        <PageHeader
          eyebrow="Return"
          title={ret.number}
          description={[
            ret.lifecycle,
            `← ${ret.sourceType}${ret.sourceNumber ? ` ${ret.sourceNumber}` : ""}`,
            ret.settleMode,
            ret.restock ? "restock" : "no restock",
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {ret.lifecycle === "posted" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void return
                </Button>
              )}
            </>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="sales_return"
          docId={returnId}
          linkPath={`/c/${companySlug}/returns/${returnId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Net</dt>
        <dd className="m-0 font-mono">{ret.netTotal}</dd>
        <dt className="text-[var(--color-muted)]">Tax</dt>
        <dd className="m-0 font-mono">{ret.taxTotal}</dd>
        <dt className="text-[var(--color-muted)]">Gross</dt>
        <dd className="m-0 font-mono font-medium">{ret.grossTotal}</dd>
        {ret.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{ret.postedAt}</dd>
          </>
        )}
      </dl>

      {ret.sourceType === "sales_order" ? (
        <p className="mt-3 text-sm">
          <RelatedDocLink
            companySlug={companySlug}
            kind="sales_order"
            docId={ret.sourceId}
          >
            {ret.sourceNumber ?? "Sales order"}
          </RelatedDocLink>
          <span className="text-[var(--color-muted)]"> · source</span>
        </p>
      ) : sourceHref ? (
        <p className="mt-3 text-sm">
          <DocNumberLink to={sourceHref}>
            {ret.sourceNumber ?? "POS ticket"}
          </DocNumberLink>
        </p>
      ) : null}

      <DataTable
        listId="return-detail-lines"
        companySlug={companySlug}
        className="mt-6 border-t border-[var(--color-border)]/90 pt-4"
        columns={returnLineColumns()}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This return has no product lines."
          />
        }
      />

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="sales_return"
        docId={returnId}
        linkPath={`/c/${companySlug}/returns/${returnId}`}
      />
    </PageFrame>
  );
}
